import { useState, useEffect, type CSSProperties } from 'react';
import { useApiQuery } from '../use-api.js';
import { DailyCostChart, type DailyCostBucket } from '../components/DailyCostChart.js';

interface CostPr {
  org: string;
  repo: string;
  pr: number;
  cost_usd: number;
  verdict_state: string;
}

interface CostResponse {
  window: { from: string; to: string };
  total_usd: number;
  total_artifacts_with_cost: number;
  by_verdict: { auto_approved_usd: number; human_required_usd: number };
  top_5_by_cost: CostPr[];
  daily: DailyCostBucket[];
}

const inputStyle: CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  color: 'var(--loop-neutral-1100)',
  background: 'var(--loop-neutral-0)'
};

const statTileStyle: CSSProperties = {
  padding: 'var(--loop-space-200)',
  flex: 1,
  minWidth: 160
};

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoStr(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function CostView() {
  const [from, setFrom] = useState(daysAgoStr(30));
  const [to, setTo] = useState(todayStr());
  const [selectedOrg, setSelectedOrg] = useState<string>('');

  const enterpriseOrgs = useApiQuery<{ orgs: Array<{ login: string; opted_in: boolean }> }>(
    ['enterprise-orgs'],
    '/api/enterprise-orgs'
  );
  const optedInOrgs = (enterpriseOrgs.data?.orgs ?? []).filter((o) => o.opted_in).map((o) => o.login).sort();

  useEffect(() => {
    if (selectedOrg === '' && optedInOrgs.length > 0) setSelectedOrg('__ALL__');
  }, [optedInOrgs, selectedOrg]);

  const path = selectedOrg === '__ALL__'
    ? `/api/cost?from=${from}&to=${to}`
    : `/api/cost?org=${encodeURIComponent(selectedOrg)}&from=${from}&to=${to}`;
  const { data, isLoading, error } = useApiQuery<CostResponse>(
    ['cost', selectedOrg, from, to],
    path,
    { enabled: selectedOrg !== '' }
  );

  function fmt(n: number | undefined): string {
    if (n === undefined || n === null) return '—';
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // Surface failure modes around the orgs query explicitly so the page never
  // hangs on "Loading…" without explanation:
  //   - orgs query errored → "Failed to load orgs"
  //   - orgs loaded but zero opted-in → "No opted-in orgs"
  //   - orgs still loading → loader (handled in JSX below)
  const orgsError = enterpriseOrgs.error;
  const orgsReady = !enterpriseOrgs.isLoading && !orgsError;
  const noOptedInOrgs = orgsReady && optedInOrgs.length === 0;

  return (
    <div>
      <h2 style={{ fontSize: '1.125rem', fontWeight: 664, marginBottom: 16, color: 'var(--loop-neutral-1100)' }}>Cost View</h2>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>Org</label>
          {/* `__ALL__` sentinel (not empty string): the orgs-loaded useEffect below
              fires on selectedOrg==='' and would auto-overwrite "All orgs" with the
              first opted-in org on every mount. */}
          <select
            value={selectedOrg}
            onChange={(e) => setSelectedOrg(e.target.value)}
            style={{ ...inputStyle, minWidth: 160 }}
            disabled={enterpriseOrgs.isLoading || noOptedInOrgs || !!orgsError}
          >
            <option value="__ALL__">All orgs</option>
            {optedInOrgs.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
        </div>
      </div>

      {orgsError && (
        <p className="error">
          Failed to load orgs: {orgsError instanceof Error ? orgsError.message : String(orgsError)}
        </p>
      )}
      {noOptedInOrgs && (
        <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>
          No opted-in orgs. Opt one in via the <a href="/config" style={{ color: 'var(--loop-brand-600)' }}>Configuration</a> page.
        </p>
      )}
      {enterpriseOrgs.isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading orgs…</p>}
      {selectedOrg !== '' && isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading cost data…</p>}
      {selectedOrg !== '' && error && <p className="error">Error: {error instanceof Error ? error.message : String(error)}</p>}

      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
            <div className="card" style={statTileStyle}>
              <div style={{ color: 'var(--loop-neutral-600)', fontSize: '0.75rem', marginBottom: 4 }}>Total spend</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 664, color: 'var(--loop-neutral-1100)' }}>{fmt(data.total_usd)}</div>
            </div>
            <div className="card" style={statTileStyle}>
              <div style={{ color: 'var(--loop-neutral-600)', fontSize: '0.75rem', marginBottom: 4 }}>Artifacts with cost</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 664, color: 'var(--loop-neutral-1100)' }}>{data.total_artifacts_with_cost ?? '—'}</div>
            </div>
            <div className="card" style={statTileStyle}>
              <div style={{ color: 'var(--loop-neutral-600)', fontSize: '0.75rem', marginBottom: 4 }}>Auto-approved</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 664, color: 'var(--loop-neutral-1100)' }}>{fmt(data.by_verdict.auto_approved_usd)}</div>
            </div>
            <div className="card" style={statTileStyle}>
              <div style={{ color: 'var(--loop-neutral-600)', fontSize: '0.75rem', marginBottom: 4 }}>Human-required</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 664, color: 'var(--loop-neutral-1100)' }}>{fmt(data.by_verdict.human_required_usd)}</div>
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <DailyCostChart daily={data.daily ?? []} />
          </div>

          <h3 style={{ marginBottom: 8, fontSize: '0.875rem', fontWeight: 664, color: 'var(--loop-neutral-900)' }}>Top 5 most expensive PRs</h3>
          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
              <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
                <tr>
                  {['Org', 'Repo', 'PR', 'Verdict', 'Cost'].map((h, i) => (
                    <th
                      key={h}
                      style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', fontWeight: 528, textAlign: i === 4 ? 'right' : 'left' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.top_5_by_cost ?? []).length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--loop-neutral-500)' }}>No data.</td>
                  </tr>
                )}
                {(data.top_5_by_cost ?? []).map(p => (
                  <tr key={`${p.org}/${p.repo}/${p.pr}`} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                    <td style={{ padding: '8px 16px' }}>{p.org}</td>
                    <td style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{p.repo}</td>
                    <td style={{ padding: '8px 16px' }}>
                      <a
                        href={`https://github.com/${p.org}/${p.repo}/pull/${p.pr}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--loop-blue-600)' }}
                      >
                        #{p.pr}
                      </a>
                    </td>
                    <td style={{ padding: '8px 16px' }}>{p.verdict_state}</td>
                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>{fmt(p.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


        </>
      )}
    </div>
  );
}
